import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, input, OnChanges, OnDestroy, SimpleChanges, DestroyRef, inject, signal, computed, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { IStep } from '../../../../shared/interfaces/step.interface';
import { FireStoreService } from '../../../../shared/services/firestore.service';
import { SingleMapService } from '../../../../shared/services/single-map.service';
import { StateService } from '../../../../shared/services/state.service';
import { EnvConfigService } from '../../../../shared/services/env-config.service';
import { NearestPointOnRoute } from '../../../../shared/services/nearest-point-on-route.service';
import { mapAlertType } from '../../../../shared/custom/mapAlertType';
import { formatUnixTimestamp } from '../../../../shared/utilities/utility-functions';
import moment from 'moment';

interface DriverLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
  speed?: number; //MPH
  heading?: number;
  locationsHistory?: Array<{ latitude: number; longitude: number; timestamp: number }>;
}

interface BreadcrumbPoint {
  position: google.maps.LatLng;
  timestamp: number;
  marker: google.maps.marker.AdvancedMarkerElement;
}

// Minimal typings for the Maps JS Routes library `Route` class (not yet in installed @types/google.maps)
interface ComputeRoutesLibrary {
  Route: {
    computeRoutes(request: {
      origin: google.maps.LatLngLiteral;
      destination: google.maps.LatLngLiteral;
      intermediates?: Array<{ location: google.maps.LatLngLiteral }>;
      travelMode: 'DRIVING';
      fields: string[];
    }): Promise<{ routes?: Array<{ path?: Array<{ lat: number; lng: number }>; viewport?: google.maps.LatLngBounds | null }> }>;
  };
}

@Component({
  selector: 'app-ride-details-map-pro',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ride-details-map-pro.component.html',
  styleUrls: ['./ride-details-map-pro.component.scss'],
})
export class RideDetailsMapProComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly stateService = inject(StateService);
  private readonly firestoreService = inject(FireStoreService);
  private readonly singleMapService = inject(SingleMapService);
  private readonly env = inject(EnvConfigService);

  private readonly destroyRef = inject(DestroyRef);

  rideMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
  rideStops = input.required<IStep[]>();

  // Computed signal to filter out skipped stops in real-time
  filteredRideStops = computed(() => {
    const stops = this.rideStops();
    const filtered = stops.filter(stop => stop.statusType?.toLowerCase() !== 'skipped');
    return filtered;
  });

  rideId = input.required<number>();
  rideDOT = input.required<string>();
  timezoneOffsetInSeconds = input.required<number>();
  // Distinguish owners across different hosts using the same component (e.g., 'inline', 'dialog-<id>')
  ownerContext = input<string>('inline');
  carMarker?: google.maps.marker.AdvancedMarkerElement;
  displayDriverLocation = input<boolean>(true);

  // Live tracking properties
  enableLiveTracking = input<boolean>(false);
  autoZoom = input<boolean>(false);
  showBreadcrumbs = input<boolean>(true);
  showSuggestedRoute = input<boolean>(false); // Flag to control suggested route visibility on deviation
  maxBreadcrumbs = input<number>(50);
  zoomLevel = input<number>(this.env.getConfig().rideMapZoomLevel); // Default zoom level for following driver

  // Private tracking state
  alertType: mapAlertType = 'None';
  driverLastHeartbeatUtc: number = 0; // Driver's last heartbeat timestamp in seconds from Firestore
  private lastPoint: DriverLocation = { latitude: 0, longitude: 0, timestamp: 0 };
  private lastHeadingPoint: number = 0;
  private breadcrumbs: BreadcrumbPoint[] = [];
  lastDriverLocation?: DriverLocation;

  // Signals for template performance
  private readonly lastDriverLocationSignal = signal<DriverLocation | undefined>(undefined);

  // Computed properties for template (avoid method calls)
  lastKnownTimestamp = computed(() => {
    const location = this.lastDriverLocationSignal();
    const history = location?.locationsHistory;
    if (history && history.length > 0) {
      return history[history.length - 1].timestamp;
    }
    return null;
  });

  formattedLastKnownTime = computed(() => {
    const timestamp = this.lastKnownTimestamp();
    if (timestamp) {
      try {
        return formatUnixTimestamp(timestamp, 'hh:mm A');
      } catch (error) {
        console.error('Error formatting timestamp:', error);
        return 'Unknown time';
      }
    }
    return 'Unknown time';
  });

  private isTracking = false;
  private readonly enableDriverLocationAutoCentering = true;
  private trackingInterval?: ReturnType<typeof setInterval>;
  private alertCheckInterval?: ReturnType<typeof setInterval>; // Timer to check for stale location data
  private locationSubscription?: Subscription; // Track Firestore subscription for manual cleanup
  private readonly stoppedThreshold = 30000; // 30 seconds without movement = stopped
  private readonly routeThresholdMeters = 20; // 20 meters off-route
  private readonly alertCheckIntervalMs = 15000; // Check alert type every 15 seconds
  private lastMovementTime = Date.now();
  private plannedRouteCache: google.maps.LatLngLiteral[] = [];
  private fullRouteCache: google.maps.LatLngLiteral[] = []; // Cache the complete route from addRideStops
  private originalRouteCache: google.maps.LatLngLiteral[] = []; // Original route for history (never overwritten)
  private historyPolyline?: google.maps.Polyline; // Simple history overlay
  private routePolyline?: google.maps.Polyline; // Main route polyline (planned route - gray)
  private routeBorderPolyline?: google.maps.Polyline; // Route border polyline
  private actualRoutePolyline?: google.maps.Polyline; // Actual route based on location history (blue)
  private actualBorderPolylines: google.maps.Polyline[] = []; // Actual route border polylines
  private deviationPolylines: google.maps.Polyline[] = []; // Deviation routes (orange)
  private deviationBorderPolylines: google.maps.Polyline[] = []; // Deviation route border polylines
  private hasGoneOffRoute = false; // Track if driver has gone off-route already
  private zoomListener?: google.maps.MapsEventListener; // Store zoom listener reference
  private mapClickListener?: google.maps.MapsEventListener; // Store map click listener reference
  private carMarkerClickHandler?: () => void; // Store car marker click listener reference
  private infoWindowCloseListener?: google.maps.MapsEventListener; // Store info window close listener reference
  private deviationPolylineListeners: google.maps.MapsEventListener[] = []; // Store all deviation polyline listeners
  private readonly deviationThresholdMiles = 0.02; // 0.02 miles deviation threshold
  private driverInfoWindow?: google.maps.InfoWindow; // Store driver info window reference
  private isDriverInfoWindowOpen = false; // Track if driver info window is open
  private allInfoWindows: google.maps.InfoWindow[] = []; // Track all info windows for cleanup

  // 🔧 OPTIMIZATION: Cache to prevent unnecessary polyline redraws
  private lastProcessedHistoryLength = 0; // Track number of points already rendered
  private isProcessingHistory = false; // Prevent concurrent processing
  private lastHistoryHash = ''; // Hash of last processed history to avoid redundant redraws
  private pendingHistoryUpdate: ReturnType<typeof setTimeout> | null = null; // Debounce history processing
  private pendingRideStopsUpdate: ReturnType<typeof setTimeout> | null = null; // Debounce rideStops updates
  private lastRideStopsHash = ''; // Track rideStops changes to avoid redundant redraws
  private lastFilteredStopsHash = '';

  readonly mapContainerRef = viewChild.required<ElementRef<HTMLDivElement>>('mapDiv');
  private mapInstance: google.maps.Map | null = null; // Local ref to the map
  private isMapAttached = false; // Track attachment state for this component
  private mapOwnerId(): string {
    return `ride-details-map-pro:${this.ownerContext()}:${this.rideId()}`;
  }

  //@region Hooks

  ngAfterViewInit(): void {
    // Attempt to attach the map once the component's view (and #mapDiv) is ready
    this.attachAndSetupMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.handleRideIdChange(changes);
    this.handleMapAttachedChanges(changes);
  }

  private handleRideIdChange(changes: SimpleChanges) {
    if (changes['rideId']?.currentValue != changes['rideId']?.previousValue) {
      // 🔧 CRITICAL: Unsubscribe from OLD ride's Firestore subscription before starting new ride
      // This prevents multiple car markers from appearing when switching between rides
      if (this.locationSubscription) {
        this.locationSubscription.unsubscribe();
        this.locationSubscription = undefined;
      }

      // 🔧 Clear previous ride's map elements (stops, routes, car marker)
      this.clearMap(false); // Don't preserve actual route when switching rides

      // Clean up car marker from previous ride
      if (this.carMarker) {
        this.carMarker.map = null;
        this.carMarker = undefined;
      }

      this.stopLiveTracking();
      this.clearBreadcrumbs();

      // 🔧 Reset ALL tracking state variables for new ride
      this.hasGoneOffRoute = false;
      this.lastProcessedHistoryLength = 0;
      this.isProcessingHistory = false;
      this.lastHistoryHash = '';
      this.alertType = 'None'; // Reset alert type

      // Reset driver location state
      this.lastDriverLocation = undefined;
      this.lastDriverLocationSignal.set(undefined);
      this.lastPoint = { latitude: 0, longitude: 0, timestamp: 0 };
      this.lastHeadingPoint = 0;
      this.lastMovementTime = Date.now();

      // Reset stop tracking state
      this.lastRideStopsHash = '';
      this.lastFilteredStopsHash = '';

      // Clear all pending timeouts
      if (this.pendingHistoryUpdate) {
        clearTimeout(this.pendingHistoryUpdate);
        this.pendingHistoryUpdate = null;
      }
      if (this.pendingRideStopsUpdate) {
        clearTimeout(this.pendingRideStopsUpdate);
        this.pendingRideStopsUpdate = null;
      }

      // Always redraw stops when switching rides
      this.addRideStops();

      // Start live tracking only if enabled
      if (this.displayDriverLocation() && this.enableLiveTracking()) {
        this.alertType = 'N/A';
        this.startLiveTracking();
      }
    }
  }

  private handleMapAttachedChanges(changes: SimpleChanges) {
    // React to input changes *after* the map has been attached
    if (!this.isMapAttached || !this.mapInstance) {
      return;
    }

    // 🔧 FIX: Detect stop changes and handle skipped stops for route recalculation
    if (changes['rideStops'] && !changes['rideStops'].firstChange) {
      // Create a hash of the current rideStops to detect actual changes
      const currentStops = this.rideStops();
      const stopsHash = currentStops.map(s => `${s.stopLocation?.latitude}-${s.stopLocation?.longitude}-${s.statusType}`).join('|');

      // Create a hash of filtered stops (non-skipped) to detect when route needs recalculation
      const filteredStops = this.filteredRideStops();
      const filteredStopsHash = filteredStops.map(s => `${s.stopLocation?.latitude}-${s.stopLocation?.longitude}`).join('|');

      // Check if the filtered stops changed (a stop was skipped/unskipped)
      const routeNeedsRecalculation = filteredStopsHash !== this.lastFilteredStopsHash && this.lastFilteredStopsHash !== '';

      // Only update if stops actually changed
      if (stopsHash !== this.lastRideStopsHash) {
        this.lastRideStopsHash = stopsHash;
        this.lastFilteredStopsHash = filteredStopsHash;

        // Cancel any pending update
        if (this.pendingRideStopsUpdate) {
          clearTimeout(this.pendingRideStopsUpdate);
        }

        // Debounce the update to batch rapid changes
        this.pendingRideStopsUpdate = setTimeout(() => {
          // Update marker styling
          this.updateStopMarkerStyles();

          // 🔧 If a stop was skipped/unskipped, recalculate the planned route
          if (routeNeedsRecalculation && !this.isRideCompleted()) {
            this.recalculatePlannedRoute();
          }

          this.pendingRideStopsUpdate = null;
        }, 300);
      }
    }

    this.handleLiveTrackingChanges(changes);
  }

  private handleLiveTrackingChanges(changes: SimpleChanges) {
    if (changes['displayDriverLocation'] || changes['enableLiveTracking']) {
      if (this.displayDriverLocation() && this.enableLiveTracking()) {
        this.alertType = 'N/A';
        this.startLiveTracking();
      } else {
        this.stopLiveTracking();
      }
    }
  }

  ngOnDestroy() {
    this.cleanupMapResources();
  }
  //@endregion

  //@region Map Setup

  attachAndSetupMap() {
    const mapContainerRef = this.mapContainerRef();
    if (this.isMapAttached || !mapContainerRef?.nativeElement) return; // Prevent double attach or if view not ready
    if (!this.singleMapService.isMapInitialized()) {
      console.error('RideDetailsMap: Map service not ready!');
      return; // Or retry later
    }

    this.mapInstance = this.singleMapService.acquireMap(this.mapOwnerId(), mapContainerRef.nativeElement);

    if (this.mapInstance) {
      this.isMapAttached = true;
      this.addRideStops();
      this.setupMapForLiveTracking();

      if (this.displayDriverLocation() && this.enableLiveTracking()) {
        this.alertType = 'N/A';
        this.startLiveTracking();
      }
    } else {
      console.error('RideDetailsMap: Failed to attach map.');
    }
  }

  private setupMapForLiveTracking() {
    if (!this.mapInstance) return;

    // Remove existing zoom listener if any
    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
    }

    // Add zoom change listener to update polyline width AND car marker size
    this.zoomListener = this.mapInstance.addListener('zoom_changed', () => {
      this.updatePolylineWidth();
      this.updateCarMarkerSize();
    });

    // Add map click listener to close all info windows
    if (this.mapClickListener) {
      google.maps.event.removeListener(this.mapClickListener);
    }
    this.mapClickListener = this.mapInstance.addListener('click', () => {
      this.closeAllInfoWindows();
    });
  }

  private updateCarMarkerSize() {
    if (!this.mapInstance || !this.carMarker || !this.lastDriverLocation) return;

    const zoom = this.mapInstance.getZoom() ?? 15;
    const carSize = this.getCarSizeForZoom(zoom);
    const isStopped = this.isDriverStopped();

    // Recreate car icon with new size (centering is handled in createCarIcon)
    const content = this.createCarIcon(this.lastDriverLocation.heading, isStopped, carSize);
    this.carMarker.replaceChildren(content);
  }

  private updatePolylineWidth() {
    if (!this.mapInstance) return;

    // Calculate stroke weight based on zoom level
    const zoom = this.mapInstance.getZoom() ?? 15;
    const strokeWeight = this.getStrokeWeightForZoom(zoom);

    // Update planned route border polyline (gray)
    if (this.routeBorderPolyline) {
      this.routeBorderPolyline.setOptions({
        strokeWeight: strokeWeight + 2,
      });
    }

    // Update planned route main polyline (gray)
    if (this.routePolyline) {
      this.routePolyline.setOptions({
        strokeWeight: strokeWeight,
      });
    }

    // Update actual route border polylines (darker blue)
    this.actualBorderPolylines.forEach(polyline => {
      polyline.setOptions({
        strokeWeight: strokeWeight + 2,
      });
    });

    // Update actual route polyline (blue)
    if (this.actualRoutePolyline) {
      this.actualRoutePolyline.setOptions({
        strokeWeight: strokeWeight,
      });
    }

    // Update deviation border polylines (darker orange)
    this.deviationBorderPolylines.forEach(polyline => {
      polyline.setOptions({
        strokeWeight: strokeWeight + 2,
      });
    });

    // Update deviation polylines (orange/blue segments)
    this.deviationPolylines.forEach(polyline => {
      polyline.setOptions({
        strokeWeight: strokeWeight,
      });
    });
  }

  private createRoutePolylines(path: google.maps.LatLngLiteral[]) {
    // Remove existing polylines
    if (this.routeBorderPolyline) {
      this.routeBorderPolyline.setMap(null);
    }
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
    }

    const zoom = this.mapInstance?.getZoom() ?? 15;
    const strokeWeight = this.getStrokeWeightForZoom(zoom);

    // 🩶 PLANNED ROUTE - Draw in GRAY (solid)
    // Create border polyline (darker gray, thicker)
    this.routeBorderPolyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#5A5F6A', // Darker gray for border
      strokeOpacity: 1,
      strokeWeight: strokeWeight + 2, // 2px thicker than main line
      zIndex: 4,
      map: this.mapInstance,
    });

    // Create main polyline (lighter gray, on top)
    this.routePolyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#8B9199', // Gray for planned route
      strokeOpacity: 1,
      strokeWeight: strokeWeight,
      zIndex: 5,
      map: this.mapInstance,
    });
  }

  private async processLocationHistory(locationsHistory: Array<{ latitude: number; longitude: number; timestamp: number }>) {
    if (!locationsHistory || locationsHistory.length === 0 || !this.mapInstance) {
      return;
    }

    // 🔧 DEBOUNCE: Cancel any pending update and schedule a new one
    // This prevents rapid consecutive calls from causing flickering
    if (this.pendingHistoryUpdate) {
      clearTimeout(this.pendingHistoryUpdate);
    }

    // 🔧 HASH CHECK: Create a simple hash of the history to detect actual changes
    const historyHash = `${locationsHistory.length}-${locationsHistory.at(-1)?.latitude?.toFixed(5)}-${locationsHistory.at(-1)?.longitude?.toFixed(5)}`;

    // If history hasn't actually changed (same hash), skip the redraw entirely
    // BUT only if we've already processed this exact history (lastProcessedHistoryLength > 0)
    if (historyHash === this.lastHistoryHash && this.actualRoutePolyline && this.lastProcessedHistoryLength > 0) {
      return;
    }

    // 🔧 OPTIMIZATION: Only redraw when there are enough NEW points
    // Calculate how many NEW points have been added since last render
    const newPointsCount = locationsHistory.length - this.lastProcessedHistoryLength;
    const minNewPointsForUpdate = 1; // Update on every new point to keep car and route synchronized

    // Skip redraw if:
    // 1. Not enough new points
    // 2. We already have polylines rendered
    // 3. We've already processed some history (not the initial load)
    // Other protections (throttleTime, hash check, debounce) prevent excessive redraws
    if (newPointsCount < minNewPointsForUpdate && this.actualRoutePolyline && this.lastProcessedHistoryLength > 0) {
      // Keep existing polylines - DON'T redraw
      return;
    }

    // 🔧 CRITICAL: Make a deep copy of the locations history to avoid stale reference issues
    const locationsHistoryCopy = locationsHistory.map(loc => ({ ...loc }));

    // 🔧 DEBOUNCE: Use shorter delay for initial draw (no existing polyline), longer for updates
    const debounceTime = this.actualRoutePolyline ? 100 : 50;

    this.pendingHistoryUpdate = setTimeout(async () => {
      await this.executeHistoryProcessing(locationsHistoryCopy, historyHash);
    }, debounceTime);
  }

  private async executeHistoryProcessing(locationsHistory: Array<{ latitude: number; longitude: number; timestamp: number }>, historyHash: string) {
    if (!this.mapInstance) return;

    // Mark as processing to prevent concurrent calls
    this.isProcessingHistory = true;

    try {
      // Update tracking variables
      this.lastProcessedHistoryLength = locationsHistory.length;
      this.lastHistoryHash = historyHash;

      // 🔧 CRITICAL: Only clear and redraw when we have enough new data
      // This prevents blinking during normal updates

      // Clear existing actual route and deviation polylines
      if (this.actualRoutePolyline) {
        this.actualRoutePolyline.setMap(null);
        this.actualRoutePolyline = undefined;
      }
      this.actualBorderPolylines.forEach(polyline => polyline.setMap(null));
      this.actualBorderPolylines = [];

      // Remove all deviation polyline listeners before clearing polylines
      this.deviationPolylineListeners.forEach(listener => google.maps.event.removeListener(listener));
      this.deviationPolylineListeners = [];

      this.deviationPolylines.forEach(polyline => polyline.setMap(null));
      this.deviationPolylines = [];

      this.deviationBorderPolylines.forEach(polyline => polyline.setMap(null));
      this.deviationBorderPolylines = [];

      const zoom = this.mapInstance.getZoom() ?? 15;
      const strokeWeight = this.getStrokeWeightForZoom(zoom);

      // Convert filtered location history to LatLng array
      const actualPath: google.maps.LatLngLiteral[] = locationsHistory.map(loc => ({
        lat: loc.latitude,
        lng: loc.longitude,
      }));

      // Calculate deviations and split path into segments (now async - waits for route)
      const segments = await this.calculateDeviationSegments(actualPath);

      // Draw segments with appropriate colors
      segments.forEach(segment => {
        if (segment.isDeviation) {
          // 🟧 DEVIATION ROUTE - Draw border first (darker orange), then main line (orange)
          const deviationBorderPolyline = new google.maps.Polyline({
            path: segment.path,
            geodesic: true,
            strokeColor: '#562F00', // Darker orange for border
            strokeOpacity: 1,
            strokeWeight: strokeWeight + 2, // 2px thicker than main line
            zIndex: 8,
            map: this.mapInstance,
          });
          this.deviationBorderPolylines.push(deviationBorderPolyline);

          const deviationPolyline = new google.maps.Polyline({
            path: segment.path,
            geodesic: true,
            strokeColor: '#FF8C00', // Orange for deviation
            strokeOpacity: 1,
            strokeWeight: strokeWeight,
            zIndex: 9, // Higher than border
            map: this.mapInstance,
          });
          this.deviationPolylines.push(deviationPolyline);

          // Add tooltip showing deviation distance at clicked point
          if (this.mapInstance) {
            // Add click listener to show tooltip with distance at clicked location
            const listener = deviationPolyline.addListener('click', (event: google.maps.MapMouseEvent) => {
              // Use original route for deviation distance calculation
              const routeToCompare = this.originalRouteCache.length > 0 ? this.originalRouteCache : this.fullRouteCache;

              if (event.latLng && routeToCompare.length > 0) {
                // Close all existing info windows first
                this.closeAllInfoWindows();

                // Calculate distance from clicked point to ORIGINAL planned route
                const clickedPoint = {
                  lat: event.latLng.lat(),
                  lng: event.latLng.lng(),
                };
                const { distance } = NearestPointOnRoute(clickedPoint, routeToCompare);
                const distanceMiles = (distance / 1609.34).toFixed(2);

                // Create new info window
                const infoWindow = new google.maps.InfoWindow();
                this.allInfoWindows.push(infoWindow); // Track it for cleanup

                infoWindow.setContent(`
                <div class="map-tooltip-content">
                  <span class="label-medium-medium text-inverse-color">${distanceMiles} miles off route</span>
                </div>
              `);
                infoWindow.setPosition(event.latLng);
                infoWindow.open(this.mapInstance);
              }
            });
            this.deviationPolylineListeners.push(listener); // Store listener for cleanup
          }
        } else {
          // 🔵 ACTUAL ROUTE - Draw border first (darker blue), then main line (blue)
          const actualBorderPolyline = new google.maps.Polyline({
            path: segment.path,
            geodesic: true,
            strokeColor: '#1565C0', // Darker blue for border
            strokeOpacity: 1,
            strokeWeight: strokeWeight + 2, // 2px thicker than main line
            zIndex: 10, // Below main line
            map: this.mapInstance,
          });
          this.actualBorderPolylines.push(actualBorderPolyline);

          const actualPolyline = new google.maps.Polyline({
            path: segment.path,
            geodesic: true,
            strokeColor: '#2196F3', // Bright blue for actual route
            strokeOpacity: 1,
            strokeWeight: strokeWeight,
            zIndex: 11, // Above border
            map: this.mapInstance,
          });

          // Store first segment as main actual route polyline for zoom updates
          if (!this.actualRoutePolyline) {
            this.actualRoutePolyline = actualPolyline;
          } else {
            // Additional segments go into deviation array for cleanup
            this.deviationPolylines.push(actualPolyline);
          }
        }
      });
    } finally {
      // Always reset processing flag
      this.isProcessingHistory = false;
    }
  }

  private async calculateDeviationSegments(actualPath: google.maps.LatLngLiteral[]): Promise<Array<{ path: google.maps.LatLngLiteral[]; isDeviation: boolean; maxDistance?: number }>> {
    // Always use ORIGINAL route for deviation comparison, not the recalculated route
    // This prevents old tracking points from being marked as deviations after route recalculation
    let routeToCompare = this.originalRouteCache.length > 0 ? this.originalRouteCache : this.fullRouteCache;

    console.log(`🔍 calculateDeviationSegments: actualPath.length=${actualPath.length}, routeToCompare.length=${routeToCompare.length}, originalCache=${this.originalRouteCache.length}, fullCache=${this.fullRouteCache.length}`);

    // 🔧 WAIT for route to be loaded if it's empty (max 5 seconds)
    if (routeToCompare.length === 0) {
      console.log('⏳ Waiting for route to load...');
      routeToCompare = await this.waitRouteLoading(routeToCompare);
      console.log(`✅ After waiting: routeToCompare.length=${routeToCompare.length}`);
    }

    if (routeToCompare.length === 0) {
      // No planned route to compare - return entire path as actual route (blue)
      // This can happen if location history is processed before the route is loaded
      console.warn('⚠️ No planned route available for deviation comparison - returning all as non-deviation (blue)');
      return [{ path: actualPath, isDeviation: false }];
    }

    // 🔧 Find the index where the driver reached the first stop (within 100m proximity)
    const firstStopReachedIndex = this.findFirstStopReachedIndex(actualPath);

    const deviationThresholdMeters = this.deviationThresholdMiles * 1609.34; // Convert miles to meters
    const segments: Array<{ path: google.maps.LatLngLiteral[]; isDeviation: boolean; maxDistance?: number }> = [];
    let currentSegment: google.maps.LatLngLiteral[] = [];
    let isCurrentSegmentDeviation = false;
    let maxDistanceInSegment = 0;

    // 🔧 FIX: Draw pre-first-stop path as BLUE (no deviation check)
    // Deviation checking only starts AFTER reaching the first stop

    if (firstStopReachedIndex === -1) {
      // First stop not reached yet - draw entire path as blue (no deviation)
      console.log('🔵 First stop not reached - drawing entire path as blue (no deviation check)');
      return [{ path: actualPath, isDeviation: false }];
    }

    // 🔵 Add pre-first-stop segment as BLUE (no deviation check)
    if (firstStopReachedIndex > 0) {
      const preFirstStopPath = actualPath.slice(0, firstStopReachedIndex + 1); // Include the first stop point for continuity
      segments.push({
        path: preFirstStopPath,
        isDeviation: false, // Always blue - no deviation check before first stop
      });
      console.log(`🔵 Pre-first-stop segment: ${preFirstStopPath.length} points (always blue)`);
    }

    // 🔶 Process points AFTER first stop with deviation checking
    const pointsToProcess = actualPath.slice(firstStopReachedIndex);
    console.log(`✂️ Processing ${pointsToProcess.length} points for deviation (after first stop)`);

    pointsToProcess.forEach((point, index) => {
      // Check for deviation for all points AFTER first stop
      const { distance } = NearestPointOnRoute(point, routeToCompare);
      const isDeviation = distance > deviationThresholdMeters;

      // Track maximum distance in deviation segment
      if (isDeviation && distance > maxDistanceInSegment) {
        maxDistanceInSegment = distance;
      }

      if (index === 0) {
        // Start first segment
        currentSegment.push(point);
        isCurrentSegmentDeviation = isDeviation;
      } else if (isDeviation === isCurrentSegmentDeviation) {
        // Continue current segment
        currentSegment.push(point);
      } else {
        // Deviation status changed - save current segment and start new one
        if (currentSegment.length > 0) {
          segments.push({
            path: [...currentSegment],
            isDeviation: isCurrentSegmentDeviation,
            maxDistance: isCurrentSegmentDeviation ? maxDistanceInSegment : undefined,
          });
        }
        currentSegment = [pointsToProcess[index - 1], point]; // Include previous point for continuity
        isCurrentSegmentDeviation = isDeviation;
        maxDistanceInSegment = isDeviation ? distance : 0;
      }
    });

    // Add final segment
    if (currentSegment.length > 0) {
      segments.push({
        path: currentSegment,
        isDeviation: isCurrentSegmentDeviation,
        maxDistance: isCurrentSegmentDeviation ? maxDistanceInSegment : undefined,
      });
    }

    // Log segment summary
    const deviationSegments = segments.filter(s => s.isDeviation);
    const normalSegments = segments.filter(s => !s.isDeviation);
    console.log(`📊 Segments: ${segments.length} total (${normalSegments.length} blue, ${deviationSegments.length} orange)`);

    return segments;
  }

  private async waitRouteLoading(routeToCompare: google.maps.LatLngLiteral[]) {
    const maxWaitTime = 5000; // 5 seconds max wait
    const checkInterval = 100; // Check every 100ms
    let elapsedTime = 0;

    while (routeToCompare.length === 0 && elapsedTime < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      routeToCompare = this.originalRouteCache.length > 0 ? this.originalRouteCache : this.fullRouteCache;
      elapsedTime += checkInterval;
    }
    return routeToCompare;
  }

  private findFirstStopReachedIndex(actualPath: google.maps.LatLngLiteral[]): number {
    const stops = this.filteredRideStops();
    if (stops.length === 0) return -1;

    const firstStop = stops[0];
    const firstStopLocation = {
      lat: firstStop.stopLocation.latitude ?? 0,
      lng: firstStop.stopLocation.longitude ?? 0,
    };
    const proximityThresholdMeters = 100;

    console.log(`📍 First stop location: (${firstStopLocation.lat}, ${firstStopLocation.lng}), threshold: ${proximityThresholdMeters}m`);

    // Find the first point that is within proximity of the first stop
    for (let i = 0; i < actualPath.length; i++) {
      const point = actualPath[i];
      const distance = this.calculateDistance(point.lat, point.lng, firstStopLocation.lat, firstStopLocation.lng);

      if (distance <= proximityThresholdMeters) {
        console.log(`✅ First stop reached at index ${i} (distance: ${distance.toFixed(2)}m), filtering ${i} pre-stop points`);
        return i;
      }
    }

    console.warn(`⚠️ First stop NOT reached in location history (all ${actualPath.length} points checked)`);
    return -1;
  }

  private getStrokeWeightForZoom(zoom: number): number {
    if (zoom <= 10) {
      return 2;
    } else if (zoom <= 12) {
      return 3;
    } else if (zoom <= 14) {
      return 4;
    } else if (zoom <= 16) {
      return 6;
    } else if (zoom <= 18) {
      return 8;
    } else {
      return 10;
    }
  }

  private getCarSizeForZoom(zoom: number): number {
    // Car icon size based on zoom level to cover the route
    if (zoom <= 10) {
      return 28;
    } else if (zoom <= 12) {
      return 36;
    } else if (zoom <= 14) {
      return 48;
    } else if (zoom <= 16) {
      return 64;
    } else {
      return 80;
    }
  }

  private startLiveTracking() {
    if (this.isTracking) return;

    this.isTracking = true;
    this.listenToDriverLocation();

    // Start monitoring for stops/delays
    this.trackingInterval = globalThis.setInterval(() => {
      this.checkForStopsOrDelays();
      if (this.isRideClosed()) {
        this.hideCarMarker();
      }
    }, 2000); // Check every 2 seconds

    // Start alert type check interval (checks every 15 seconds for stale data)
    this.alertCheckInterval = globalThis.setInterval(() => {
      if (this.lastDriverLocation?.locationsHistory) {
        console.log('⏰ Alert check interval: Checking for stale location data...');
        this.updateAlertType(this.lastDriverLocation.locationsHistory);
      }
    }, this.alertCheckIntervalMs);
  }

  private hideCarMarker() {
    if (this.carMarker) {
      this.carMarker.map = null;
      this.carMarker = undefined;
    }
    this.closeDriverLocationTooltip();
  }
  addRideStops() {
    if (!this.isMapAttached || !this.mapInstance) return;

    // 🔧 FIX: Always preserve actual route polylines when updating stops
    // We should only clear actual route when switching to a different ride (handled in handleRideIdChange)
    // This prevents the actual route from disappearing when stop statuses change
    const shouldPreserveActualRoute = this.actualRoutePolyline !== undefined || this.isRideCompleted();
    this.clearMap(shouldPreserveActualRoute);

    const allStops = this.rideStops();
    if (!allStops || allStops.length === 0) return;

    // Draw ALL stops (including skipped ones) as markers
    allStops.forEach((stop: IStep, i: number) => {
      const isSkipped = stop.statusType?.toLowerCase() === 'skipped';

      const markerLabel = document.createElement('div');
      const iconClass = stop.placeType == 'Home' ? 'icon-house-solid' : 'icon-building-apartment-solid';
      markerLabel.className = 'flex flex-column align-items-center';

      // Apply gray styling for skipped stops
      const markerStyle = isSkipped ? 'style="filter: grayscale(100%);"' : '';
      const stopMarkerClass = isSkipped ? 'stop-marker-skipped' : `stop-marker-${stop.statusType?.toLowerCase()}`;

      markerLabel.innerHTML = `
        <div class="icon-container relative position-relative" ${markerStyle}>
          <img src="/assets/images/Union.svg" style="width: 40px; height: 40px" alt="Stop Marker" />
          <i class="${iconClass} text-inverse-color size-icon-sm"
            style="position: absolute;top: 36%;left: 50%;translate: -50% -50%;"></i>
        </div>
        <div class="stop-marker ${stopMarkerClass}">
          ${i + 1}
        </div>
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: stop.stopLocation.latitude ?? 0, lng: stop.stopLocation.longitude ?? 0 },
        map: this.mapInstance,
        content: markerLabel,
        title: `${i + 1}${isSkipped ? ' (Skipped)' : ''}`,
      });
      this.rideMarkers.push(marker);
    });

    // Connect only non-skipped stops for route calculation
    // 🔧 FIX: Always calculate planned route for deviation comparison, even for completed rides
    // For completed rides, we calculate the route but don't draw the grey line (skipDrawing=true)
    // This ensures the cache is populated for deviation detection
    const isCompleted = this.isRideCompleted();
    if (this.originalRouteCache.length === 0) {
      // No cache yet - calculate the planned route (skip drawing for completed rides)
      this.connectStops(this.mapInstance, this.filteredRideStops(), isCompleted);
    } else if (!isCompleted && this.routePolyline === undefined) {
      // Not completed and route was cleared but we have cache - redraw from cache
      this.createRoutePolylines(this.originalRouteCache);
    }
    // For completed rides with cache, we don't redraw the grey line
  }

  /**
   * Updates only the stop marker styles without clearing the map
   * This preserves the actual route polyline while updating marker appearance
   */
  private updateStopMarkerStyles() {
    const allStops = this.rideStops();
    if (!allStops || allStops.length === 0) return;

    // Update each marker's content to reflect new status
    allStops.forEach((stop: IStep, i: number) => {
      if (i >= this.rideMarkers.length) return; // Safety check

      const marker = this.rideMarkers[i];
      const isSkipped = stop.statusType?.toLowerCase() === 'skipped';

      const markerLabel = document.createElement('div');
      const iconClass = stop.placeType == 'Home' ? 'icon-house-solid' : 'icon-building-apartment-solid';
      markerLabel.className = 'flex flex-column align-items-center';

      // Apply gray styling for skipped stops
      const markerStyle = isSkipped ? 'style="filter: grayscale(100%);"' : '';
      const stopMarkerClass = isSkipped ? 'stop-marker-skipped' : `stop-marker-${stop.statusType?.toLowerCase()}`;

      markerLabel.innerHTML = `
        <div class="icon-container relative position-relative" ${markerStyle}>
          <img src="/assets/images/Union.svg" style="width: 40px; height: 40px" alt="Stop Marker" />
          <i class="${iconClass} text-inverse-color size-icon-sm"
            style="position: absolute;top: 36%;left: 50%;translate: -50% -50%;"></i>
        </div>
        <div class="stop-marker ${stopMarkerClass}">
          ${i + 1}
        </div>
      `;

      // Update marker content (this preserves position and map attachment)
      marker.replaceChildren(markerLabel);
      marker.title = `${i + 1}${isSkipped ? ' (Skipped)' : ''}`;
    });
  }

  private async computeDrivingRoute(
    origin: google.maps.LatLngLiteral,
    destination: google.maps.LatLngLiteral,
    intermediates: google.maps.LatLngLiteral[],
  ): Promise<{ path: google.maps.LatLngLiteral[]; viewport?: google.maps.LatLngBounds | null }> {
    const { Route } = (await google.maps.importLibrary('routes')) as unknown as ComputeRoutesLibrary;
    const { routes } = await Route.computeRoutes({
      origin,
      destination,
      intermediates: intermediates.map(location => ({ location })),
      travelMode: 'DRIVING',
      fields: ['path', 'viewport'],
    });

    const route = routes?.[0];
    if (!route?.path?.length) {
      throw new Error('computeRoutes returned no route');
    }
    return { path: route.path.map(p => ({ lat: p.lat, lng: p.lng })), viewport: route.viewport };
  }

  connectStops(map: google.maps.Map, rideStops: IStep[], skipDrawing = false) {
    const origin = { lat: rideStops[0].stopLocation.latitude ?? 0, lng: rideStops[0].stopLocation.longitude ?? 0 };

    const destination = { lat: rideStops.at(-1)?.stopLocation.latitude ?? 0, lng: rideStops.at(-1)?.stopLocation.longitude ?? 0 };

    const intermediates = rideStops.slice(1, -1).map(stop => ({ lat: stop.stopLocation.latitude ?? 0, lng: stop.stopLocation.longitude ?? 0 }));

    this.computeDrivingRoute(origin, destination, intermediates)
      .then(({ path, viewport }) => {
        // Preserves the auto-fit previously done by DirectionsRenderer.setDirections()
        if (viewport) {
          map.fitBounds(viewport);
        }
        // 🔧 CACHE THE COMPLETE ROUTE for off-route detection
        this.fullRouteCache = path;

        // 🔧 ALWAYS set originalRouteCache from planned route (for deviation comparison)
        // This ensures it's populated even after browser reload or component re-initialization
        this.originalRouteCache = [...this.fullRouteCache];

        console.log(`✅ Route cached: ${this.originalRouteCache.length} points, skipDrawing=${skipDrawing}`);

        // 🔧 Only create visual polylines if not skipping drawing (e.g., for completed rides)
        if (!skipDrawing) {
          this.createRoutePolylines(this.fullRouteCache);
        }
      })
      .catch(error => {
        console.error('Failed to get directions for route display:', error);
      });
  }

  /**
   * Recalculates the planned route when stops are skipped/unskipped
   * Clears existing route polylines and recalculates based on current filtered stops
   */
  private recalculatePlannedRoute() {
    if (!this.mapInstance) return;

    const filteredStops = this.filteredRideStops();

    // Need at least 2 stops to draw a route
    if (filteredStops.length < 2) {
      // Clear route polylines if less than 2 stops
      if (this.routePolyline) {
        this.routePolyline.setMap(null);
        this.routePolyline = undefined;
      }
      if (this.routeBorderPolyline) {
        this.routeBorderPolyline.setMap(null);
        this.routeBorderPolyline = undefined;
      }
      this.fullRouteCache = [];
      // Note: Keep originalRouteCache for deviation comparison
      return;
    }

    // Clear existing route polylines before recalculating
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
      this.routePolyline = undefined;
    }
    if (this.routeBorderPolyline) {
      this.routeBorderPolyline.setMap(null);
      this.routeBorderPolyline = undefined;
    }

    // Recalculate route with filtered stops (excludes skipped)
    this.connectStops(this.mapInstance, filteredStops);

    console.log(`🔄 Recalculated planned route with ${filteredStops.length} stops (skipped stops excluded)`);
  }

  private createCarIcon(heading?: number, isStopped = false, carSize?: number): HTMLElement {
    const div = document.createElement('div');
    div.className = `car-marker ${isStopped ? 'stopped' : 'moving'}`;

    // Use heading value or default to 0 (north)
    const rotation = heading ?? 0;
    const headlightOpacity = isStopped ? '0.3' : '0.6';

    // Get car size from parameter or use current zoom level
    const zoom = this.mapInstance?.getZoom() ?? 15;
    const size = carSize ?? this.getCarSizeForZoom(zoom);

    // Scale headlight beam proportionally to car size
    const beamWidth = size * 0.83; // 83% of car size (40/48)
    const beamHeight = size * 1.25; // 125% of car size (60/48)
    const beamOffset = -size * 1.04; // Offset above car

    // Apply centering transform to the outer div
    div.style.transform = `translate(-${size / 2}px, -${size / 2}px)`;
    div.style.position = 'absolute';

    div.innerHTML = `
      <div class="car-icon-container" style="transform: rotate(${rotation}deg); position: relative; width: ${size}px; height: ${size}px; display: flex; align-items: center; justify-content: center;">

        <!-- Headlight beam (shadow light effect) -->
        <div class="car-headlight-beam" style="
          position: absolute;
          top: ${beamOffset}px;
          left: 45%;
          transform: translateX(-45%);
          width: ${beamWidth}px;
          height: ${beamHeight}px;
          background: linear-gradient(to top, #2196F3 0%, rgba(33, 150, 243, 0.4) 50%, transparent 100%);
          opacity: ${headlightOpacity};
          clip-path: polygon(40% 100%, 0% 0%, 100% 0%, 60% 100%);
          filter: blur(2px);
          z-index: 1;
        "></div>

        <!-- Car pulse effect -->
        <div class="car-pulse ${isStopped ? 'stopped' : ''}" style="
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 2;
        "></div>

        <!-- Car icon (dynamically sized to cover route) -->
        <img src="/assets/google-map-assets/icons/car.svg"
             alt="Car"
             style="
               width: ${size}px;
               height: ${size}px;
               filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
               position: relative;
               z-index: 3;
             ">
      </div>
    `;

    return div;
  }

  listenToDriverLocation() {
    if (!this.stateService.getFirebaseUser()) return;

    // 🔧 FIX: ALWAYS unsubscribe from any existing subscription before creating a new one
    // This ensures we never have multiple subscriptions creating duplicate car markers
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = undefined;
    }

    // 🔧 FIX: Clear existing car marker before starting new subscription
    if (this.carMarker) {
      this.carMarker.map = null;
      this.carMarker = undefined;
    }

    this.locationSubscription = this.firestoreService
      .listenToDocChanges(`${this.env.getConfig().mapDetailsCollection}/ride_${this.rideId()}`)
      .pipe(
        throttleTime(1000, undefined, { leading: true, trailing: true }), // Accept changes every 1 second
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: res => {
          console.log(res);
          if (!res.data?.locations_history || res.data.locations_history.length === 0) return;

          // Capture driver's last heartbeat timestamp from Firestore (always available, defaults to 0)
          this.driverLastHeartbeatUtc = res.data.driver_last_heartbeat_utc_in_seconds ?? 0;

          const _lastPoint = res.data.locations_history[res.data.locations_history.length - 1];
          if (!_lastPoint) return;

          // Check if this is a redundant location (same position as before)
          const isSamePosition = this.lastPoint && _lastPoint.latitude === this.lastPoint.latitude && _lastPoint.longitude === this.lastPoint.longitude;

          // 🔧 FIX: Always update alert type even for redundant positions
          // This prevents false GPSLost/ConnectionLost alerts when driver is stopped at traffic
          if (isSamePosition) {
            // Update the timestamp to reflect that we're still receiving data
            this.lastPoint.timestamp = _lastPoint.timestamp;
            // 🔧 Magic fix: Update the last location's timestamp in history so alertCheckInterval sees fresh data
            if (this.lastDriverLocation?.locationsHistory?.length) {
              this.lastDriverLocation.locationsHistory.at(-1)!.timestamp = _lastPoint.timestamp;
            }
            return;
          }

          if (_lastPoint.timestamp <= this.lastPoint.timestamp) {
            // No new update
            return;
          }
          if (res.data.heading && res.data.heading > 0) {
            this.lastHeadingPoint = res.data.heading;
          }

          // Calculate speed based on last 5 points from location history
          const calculatedSpeed = this.calculateSpeedFromHistory(res.data.locations_history);

          const location: DriverLocation = {
            latitude: _lastPoint.latitude,
            longitude: _lastPoint.longitude,
            timestamp: _lastPoint.timestamp,
            speed: calculatedSpeed,
            heading: this.lastHeadingPoint,
            locationsHistory: res.data.locations_history,
          };
          _lastPoint.speed = calculatedSpeed;
          this.lastPoint = _lastPoint;
          this.updateDriverLocationWithTracking(location);
        },
        error: error => {
          console.error('Driver location tracking error:', error);
        },
      });
  }

  //@endregion

  //@region Cleanup
  private stopLiveTracking() {
    this.isTracking = false;
    this.stopListeningToDriverLocation();

    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
      this.trackingInterval = undefined;
    }

    // Clear alert check interval
    if (this.alertCheckInterval) {
      clearInterval(this.alertCheckInterval);
      this.alertCheckInterval = undefined;
    }

    // Clear pending history update to prevent flickering
    if (this.pendingHistoryUpdate) {
      clearTimeout(this.pendingHistoryUpdate);
      this.pendingHistoryUpdate = null;
    }

    this.clearBreadcrumbs();
    this.closeDriverLocationTooltip(); // Close tooltip when stopping tracking
  }

  cleanupMapResources() {
    // If map was attached, clean up component additions and detach
    if (this.isMapAttached) {
      this.stopLiveTracking();
      this.clearPendingUpdates();
      this.removeAllEventListeners();
      this.cleanupAllPolylines();
      this.cleanupMarkersAndRenderers();
      this.singleMapService.releaseMap(this.mapOwnerId());
    }

    // Nullify local references
    this.mapInstance = null;
    this.isMapAttached = false;
  }

  private clearPendingUpdates() {
    if (this.pendingRideStopsUpdate) {
      clearTimeout(this.pendingRideStopsUpdate);
      this.pendingRideStopsUpdate = null;
    }
  }

  private removeAllEventListeners() {
    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
      this.zoomListener = undefined;
    }
    if (this.mapClickListener) {
      google.maps.event.removeListener(this.mapClickListener);
      this.mapClickListener = undefined;
    }
    if (this.carMarker && this.carMarkerClickHandler) {
      this.carMarker.removeEventListener('click', this.carMarkerClickHandler);
      this.carMarkerClickHandler = undefined;
    }
    if (this.infoWindowCloseListener) {
      google.maps.event.removeListener(this.infoWindowCloseListener);
      this.infoWindowCloseListener = undefined;
    }
    // Remove all deviation polyline listeners
    this.deviationPolylineListeners.forEach(listener => google.maps.event.removeListener(listener));
    this.deviationPolylineListeners = [];
  }

  private cleanupAllPolylines() {
    // Clean up all route polylines
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
      this.routePolyline = undefined;
    }
    if (this.routeBorderPolyline) {
      this.routeBorderPolyline.setMap(null);
      this.routeBorderPolyline = undefined;
    }

    if (this.actualRoutePolyline) {
      this.actualRoutePolyline.setMap(null);
      this.actualRoutePolyline = undefined;
    }
    this.actualBorderPolylines.forEach(polyline => polyline.setMap(null));
    this.actualBorderPolylines = [];

    this.deviationPolylines.forEach(polyline => polyline.setMap(null));
    this.deviationPolylines = [];

    this.deviationBorderPolylines.forEach(polyline => polyline.setMap(null));
    this.deviationBorderPolylines = [];

    // Clean up history overlay if it exists
    if (this.historyPolyline) {
      this.historyPolyline.setMap(null);
      this.historyPolyline = undefined;
    }
  }

  private cleanupMarkersAndRenderers() {
    this.rideMarkers.forEach(marker => (marker.map = null));
    this.rideMarkers = [];

    if (this.carMarker) {
      this.carMarker.map = null;
      this.carMarker = undefined;
    }
  }

  clearMap(preserveActualRoute = false) {
    this.rideMarkers.forEach(marker => (marker.map = null));
    this.rideMarkers = [];

    // Clear all route polylines
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
      this.routePolyline = undefined;
    }
    if (this.routeBorderPolyline) {
      this.routeBorderPolyline.setMap(null);
      this.routeBorderPolyline = undefined;
    }

    // Only clear actual route if NOT preserving (i.e., ride not completed)
    if (!preserveActualRoute) {
      if (this.actualRoutePolyline) {
        this.actualRoutePolyline.setMap(null);
        this.actualRoutePolyline = undefined;
      }
      this.actualBorderPolylines.forEach(polyline => polyline.setMap(null));
      this.actualBorderPolylines = [];

      this.deviationPolylines.forEach(polyline => polyline.setMap(null));
      this.deviationPolylines = [];

      this.deviationBorderPolylines.forEach(polyline => polyline.setMap(null));
      this.deviationBorderPolylines = [];

      // Reset route caches when clearing map
      this.fullRouteCache = [];
      this.originalRouteCache = [];
      this.plannedRouteCache = [];

      // Reset off-route tracking
      this.hasGoneOffRoute = false;

      // Reset history tracking for fresh redraw
      this.lastProcessedHistoryLength = 0;
      this.isProcessingHistory = false;
      this.lastHistoryHash = '';
      if (this.pendingHistoryUpdate) {
        clearTimeout(this.pendingHistoryUpdate);
        this.pendingHistoryUpdate = null;
      }

      // Clear history overlay if it exists
      if (this.historyPolyline) {
        this.historyPolyline.setMap(null);
        this.historyPolyline = undefined;
      }
    }
  }

  private clearBreadcrumbs() {
    this.breadcrumbs.forEach(breadcrumb => {
      breadcrumb.marker.map = null;
    });
    this.breadcrumbs = [];
  }

  stopListeningToDriverLocation() {
    // 🔧 Unsubscribe from Firestore location updates
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = undefined;
    }

    // Clean up car marker
    if (this.carMarker) {
      this.carMarker.map = null;
      this.carMarker = undefined;
    }

    // Close and cleanup all info windows
    this.closeAllInfoWindows();
    this.allInfoWindows = []; // Clear the array
    if (this.driverInfoWindow) {
      this.driverInfoWindow = undefined;
    }
  }

  //@endregion

  //@region Business Logic

  private updateDriverLocationWithTracking(location: DriverLocation) {
    const position = new google.maps.LatLng(location.latitude, location.longitude);

    // Update driver marker
    if (!this.isRideClosed()) {
      this.updateDriverMarker(location);
    }

    // 🔵 Process location history to draw actual route and deviations
    if (location.locationsHistory && location.locationsHistory.length > 0) {
      this.processLocationHistory(location.locationsHistory);
    }

    // 🚨 Update alert type based on location history criteria
    this.updateAlertType(location.locationsHistory);

    // Add breadcrumb if driver moved significantly
    if (this.shouldAddBreadcrumb(location)) {
      this.addBreadcrumb(position, location.timestamp);
    }

    // Update tracking state
    this.lastDriverLocation = location;
    this.lastDriverLocationSignal.set(location);

    // Check if driver is moving
    if (this.hasDriverMoved(location)) {
      this.lastMovementTime = Date.now();
    }
  }

  private updateAlertType(locationsHistory?: Array<{ latitude: number; longitude: number; timestamp: number }>) {
    // Reset to None if no location history
    if (!locationsHistory || locationsHistory.length === 0) {
      return;
    }
    this.alertType = 'N/A';
    const upcomingStops = this.getUpcomingStops();

    if (upcomingStops.length === 0) {
      return;
    }

    // Get the last location point
    const lastLocation = locationsHistory.at(-1);
    if (!lastLocation) {
      return;
    }

    let currentTime = moment.utc().valueOf(); // milliseconds
    const lastTimestamp = this.driverLastHeartbeatUtc * 1000; // Convert to milliseconds if needed
    const timeDifference = (currentTime - lastTimestamp) / 1000; // Time difference in seconds

    // Priority 1: Check for ConnectionLost (45 seconds)
    if (timeDifference >= 45 && this.driverLastHeartbeatUtc > 0) {
      this.alertType = 'ConnectionLost';
      console.log(`🔴 ConnectionLost: Last location was ${timeDifference.toFixed(0)} seconds ago`);
      return;
    }

    // Priority 2: Check for GPSLost (15 seconds)
    if (timeDifference >= 15 && this.driverLastHeartbeatUtc > 0) {
      this.alertType = 'GPSLost';
      console.log(`🟡 GPSLost: Last location was ${timeDifference.toFixed(0)} seconds ago`);
      return;
    }

    const firstStopReached = this.isFirstStopArrivingOrLater();
    if (!firstStopReached) {
      return;
    }

    // Priority 3: Check for Warning (driver has deviation)
    const hasDeviation = this.checkForDeviation(lastLocation);
    if (hasDeviation) {
      this.alertType = 'Warning';
      console.log('🟠 Warning: Driver has deviation from planned route');
    }
  }

  private checkForDeviation(lastLocation: { latitude: number; longitude: number; timestamp: number }): boolean {
    const deviationThresholdMeters = this.deviationThresholdMiles * 1609.34; // Convert miles to meters

    const point = { lat: lastLocation.latitude, lng: lastLocation.longitude };
    const { distance } = NearestPointOnRoute(point, this.originalRouteCache);

    // Return true if the last point has a deviation
    return distance > deviationThresholdMeters;
  }

  private updateDriverMarker(location: DriverLocation) {
    const position = { lat: location.latitude, lng: location.longitude };
    const isStopped = this.isDriverStopped();

    if (this.carMarker) {
      this.carMarker.position = position;
      // Always update marker content to reflect heading and stopped state
      const content = this.createCarIcon(location.heading, isStopped);
      this.carMarker.replaceChildren(content);

      // Update tooltip position only (not content) if it's open
      if (this.isDriverInfoWindowOpen && this.driverInfoWindow) {
        this.driverInfoWindow.setPosition({ lat: location.latitude, lng: location.longitude });
      }
    } else {
      // Create new marker with heading and stopped state
      const content = this.createCarIcon(location.heading, isStopped);

      this.carMarker = new google.maps.marker.AdvancedMarkerElement({
        position: position,
        map: this.mapInstance,
        content: content,
        zIndex: 1000, // Ensure car marker is on top
      });

      // Add click listener to toggle driver location info tooltip
      this.carMarkerClickHandler = () => {
        if (this.isDriverInfoWindowOpen) {
          this.closeDriverLocationTooltip();
        } else {
          this.showDriverLocationTooltip(this.lastPoint);
        }
      };
      this.carMarker.addEventListener('click', this.carMarkerClickHandler);
    }
  }

  getTooltipContent(time: string, speed: string, address: string): string {
    return `
      <div class="map-tooltip-content">
        <div class="flex flex-column gap-spacing-2xs">
          <div class="label-medium-medium text-inverse-color">
            <span>Time:</span>
            <span>${time}</span>
          </div>
          <hr class="m-0 background-fill-primary-border">
          <div class="label-medium-medium text-inverse-color">
            <span>Speed:</span>
            <span>${speed}</span>
          </div>
          <hr class="m-0 background-fill-primary-border">
          <div class="label-medium-medium text-inverse-color">
            <span>Address:</span>
            <span>${address}</span>
          </div>
        </div>
      </div>
    `;
  }

  private showDriverLocationTooltip(location: DriverLocation) {
    if (!this.mapInstance) return;

    // Close all existing info windows first
    this.closeAllInfoWindows();

    // Format time from timestamp
    const time = formatUnixTimestamp(location.timestamp);

    // Format speed
    const speed = location.speed === undefined ? 'N/A' : `${location.speed.toFixed(0)} MPH`;

    // Create or reuse info window
    if (!this.driverInfoWindow) {
      this.driverInfoWindow = new google.maps.InfoWindow();

      // Add close listener to track when user manually closes the window
      if (this.infoWindowCloseListener) {
        google.maps.event.removeListener(this.infoWindowCloseListener);
      }
      this.infoWindowCloseListener = this.driverInfoWindow.addListener('closeclick', () => {
        this.isDriverInfoWindowOpen = false;
      });
    }

    // Set initial content with loading state for address
    this.driverInfoWindow.setContent(this.getTooltipContent(time, speed, 'Loading...'));

    this.driverInfoWindow.setPosition({ lat: location.latitude, lng: location.longitude });
    this.driverInfoWindow.open(this.mapInstance);
    this.isDriverInfoWindowOpen = true;

    // Fetch address from Google Geocoding API
    this.getAddressFromCoordinates(location.latitude, location.longitude).then(address => {
      if (this.driverInfoWindow && this.isDriverInfoWindowOpen) {
        this.driverInfoWindow.setContent(this.getTooltipContent(time, speed, address));
      }
    });
  }

  private closeDriverLocationTooltip() {
    if (this.driverInfoWindow) {
      this.driverInfoWindow.close();
      this.isDriverInfoWindowOpen = false;
    }
  }

  private closeAllInfoWindows() {
    // Close driver info window
    this.closeDriverLocationTooltip();

    // Close all other info windows
    this.allInfoWindows.forEach(infoWindow => {
      infoWindow.close();
    });
  }

  private async getAddressFromCoordinates(latitude: number, longitude: number): Promise<string> {
    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({
        location: { lat: latitude, lng: longitude },
      });

      if (response.results && response.results.length > 0) {
        return response.results[0].formatted_address;
      } else {
        return 'Address not found';
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      return 'Unable to fetch address';
    }
  }

  private shouldAddBreadcrumb(location: DriverLocation): boolean {
    if (!this.showBreadcrumbs() || !this.lastDriverLocation) return true;

    // Add breadcrumb if driver moved more than 10 meters
    const distance = this.calculateDistance(this.lastDriverLocation.latitude, this.lastDriverLocation.longitude, location.latitude, location.longitude);

    return distance > 100; // meters
  }

  // Creates a small blue dot on the map at the driver's previous location
  // Each dot represents where the driver was at a specific time
  private addBreadcrumb(position: google.maps.LatLng, timestamp: number) {
    if (!this.mapInstance) return;

    // Create breadcrumb marker
    const breadcrumbDiv = document.createElement('div');
    breadcrumbDiv.className = 'breadcrumb-marker';
    breadcrumbDiv.innerHTML = `
      <div class="breadcrumb-dot"></div>
    `;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: position,
      map: this.mapInstance,
      content: breadcrumbDiv,
      zIndex: 100,
    });

    const breadcrumb: BreadcrumbPoint = {
      position,
      timestamp,
      marker,
    };

    this.breadcrumbs.push(breadcrumb);

    // Remove old breadcrumbs if we exceed the limit
    if (this.breadcrumbs.length > this.maxBreadcrumbs()) {
      const oldBreadcrumb = this.breadcrumbs.shift();
      if (oldBreadcrumb) {
        oldBreadcrumb.marker.map = null;
      }
    }

    // Fade older breadcrumbs
    this.updateBreadcrumbOpacity();
  }

  private updateBreadcrumbOpacity() {
    const now = Date.now();
    this.breadcrumbs.forEach((breadcrumb, index) => {
      const age = now - breadcrumb.timestamp;
      const maxAge = 300000; // 5 minutes
      const opacity = Math.max(0.1, 1 - age / maxAge);

      const element = breadcrumb.marker.firstElementChild as HTMLElement;
      if (element) {
        element.style.opacity = opacity.toString();
      }
    });
  }

  private autoZoomToDriver(location: DriverLocation) {
    if (!this.mapInstance) return;

    const bounds = new google.maps.LatLngBounds();
    const driverPos = new google.maps.LatLng(location.latitude, location.longitude);

    // Include driver position
    bounds.extend(driverPos);

    // Include upcoming stops
    const upcomingStops = this.getUpcomingStops();
    upcomingStops.forEach(stop => {
      bounds.extend(new google.maps.LatLng(stop.stopLocation.latitude ?? 0, stop.stopLocation.longitude ?? 0));
    });

    // Fit map to bounds with padding
    this.mapInstance.fitBounds(bounds, {
      top: 50,
      right: 50,
      bottom: 50,
      left: 50,
    });
    this.mapInstance.setZoom(this.zoomLevel());
    // Ensure minimum zoom level for detailed view
  }

  private getUpcomingStops(): IStep[] {
    // Return next 2-3 stops that haven't been completed
    return this.filteredRideStops()
      .filter(stop => stop.statusType?.toLowerCase() === 'enroute' || stop.statusType?.toLowerCase() === 'none')
      .slice(0, 3);
  }

  private isFirstStopVisitedOrSkipped(): boolean {
    // Check if the first stop has been visited or skipped
    const firstStop = this.rideStops()[0];
    return firstStop?.statusType?.toLowerCase() === 'visited' || firstStop?.statusType?.toLowerCase() === 'skipped';
  }

  private isFirstStopArrivingOrLater(): boolean {
    // Check the first NON-SKIPPED stop to see if driver has arrived
    // Skipped stops don't count as "arrived" since driver never reached them
    const firstStop = this.filteredRideStops()[0];
    const status = firstStop?.statusType?.toLowerCase();
    return status === 'arrived' || status === 'visited';
  }

  private isRideCompleted(): boolean {
    const stops = this.filteredRideStops();
    if (stops.length === 0) return false;

    // 🔧 FIX: Include 'arrived' status for the LAST stop to preserve actual route
    // when driver arrives at final destination but hasn't marked it as visited yet
    return stops.every((stop, index) => {
      const status = stop.statusType?.toLowerCase();
      const isLastStop = index === stops.length - 1;
      // For the last stop, also consider 'arrived' as complete (to preserve actual route)
      if (isLastStop) {
        return status === 'visited' || status === 'skipped' || status === 'arrived';
      }
      return status === 'visited' || status === 'skipped';
    });
  }

  private isRideClosed(): boolean {
    const stops = this.filteredRideStops();
    if (stops.length === 0) return false;
    return stops.every((stop, index) => {
      const status = stop.statusType?.toLowerCase();
      return status === 'visited' || status === 'skipped';
    });
  }

  private hasDriverMoved(location: DriverLocation): boolean {
    if (!this.lastDriverLocation) return true;

    const distance = this.calculateDistance(this.lastDriverLocation.latitude, this.lastDriverLocation.longitude, location.latitude, location.longitude);

    return distance > 20; // 20 meters threshold for movement
  }

  isDriverStopped(): boolean {
    return Date.now() - this.lastMovementTime > this.stoppedThreshold;
  }

  hasDriverLocation(): boolean {
    return !!this.lastDriverLocation;
  }

  private checkForStopsOrDelays() {
    if (!this.lastDriverLocation) return;

    // Driver stopped - zoom in for detailed view
    const currentZoom = this.mapInstance?.getZoom() ?? this.zoomLevel();

    this.zoomToDriverLocation(currentZoom);

    // Update car marker to show stopped state
    if (this.carMarker) {
      const content = this.createCarIcon(this.lastDriverLocation.heading, this.isDriverStopped());
      this.carMarker.replaceChildren(content);
    }

    //if driver is reached to a first stop
    const isFirstStopVisitedOrSkipped = this.isFirstStopVisitedOrSkipped();
    if (isFirstStopVisitedOrSkipped) {
      // Check if driver is off-route (optimized version)
      this.checkIfDriverOffRoute();
    }
  }

  // off-route detection using the SAME route displayed on map
  private checkIfDriverOffRoute() {
    if (!this.lastDriverLocation) return;

    const driverLatLng: google.maps.LatLngLiteral = {
      lat: this.lastDriverLocation.latitude,
      lng: this.lastDriverLocation.longitude,
    };

    // Use the SAME complete route that's displayed on the map
    if (this.fullRouteCache.length === 0) {
      //No complete route cached yet - off-route detection unavailable
      return;
    }

    // Use the complete cached route for accurate off-route detection
    this.performOffRouteCheck(driverLatLng);
  }

  private performOffRouteCheck(driverPos: google.maps.LatLngLiteral) {
    // Choose route for off-route detection based on whether we've already gone off-route
    let routeToUse: google.maps.LatLngLiteral[];

    if (!this.hasGoneOffRoute && this.originalRouteCache.length > 0) {
      // First time: use original route
      routeToUse = this.originalRouteCache;
    } else if (this.fullRouteCache.length > 0) {
      // After first off-route: use current recalculated route
      routeToUse = this.fullRouteCache;
    } else {
      routeToUse = this.plannedRouteCache;
    }

    if (routeToUse.length === 0) {
      //No route available for off-route check
      return;
    }

    // Use consistent distance calculation (same as your existing calculateDistance method)
    const { distance } = NearestPointOnRoute(driverPos, routeToUse);

    if (distance > this.routeThresholdMeters) {
      if (!this.hasGoneOffRoute) {
        // First time going off-route: mark it and recalculate route (no history overlay drawn)
        this.hasGoneOffRoute = true;
      }
      // Recalculate route from driver position (no visual suggested route)
      this.recalculateFromDriverPosition();
    }
  }

  // Recalculate route starting from driver's current position to remaining stops
  private recalculateFromDriverPosition() {
    if (!this.lastDriverLocation || !this.mapInstance) return;

    // Get remaining stops (not completed yet)
    const remainingStops = this.getRemainingStops();

    if (remainingStops.length === 0) {
      return;
    }

    // Create new route: driver position → remaining stops
    const driverPos = {
      lat: this.lastDriverLocation.latitude,
      lng: this.lastDriverLocation.longitude,
    };

    const destination = {
      lat: remainingStops.at(-1)?.stopLocation.latitude ?? 0,
      lng: remainingStops.at(-1)?.stopLocation.longitude ?? 0,
    };

    // All remaining stops except the last one become intermediates
    const intermediates = remainingStops.slice(0, -1).map(stop => ({
      lat: stop.stopLocation.latitude ?? 0,
      lng: stop.stopLocation.longitude ?? 0,
    }));

    // Path only - no drawing and no viewport change, so the planned route is not redrawn
    this.computeDrivingRoute(driverPos, destination, intermediates)
      .then(({ path }) => {
        // Cache the NEW route that starts from driver's position
        // This is used for real-time deviation detection after the driver goes off-route
        this.fullRouteCache = path;
      })
      .catch(error => {
        console.error('Failed to recalculate route from driver position:', error);
      });
  }

  // Get stops that haven't been completed yet
  private getRemainingStops(): IStep[] {
    // Find the first stop that is not completed (not 'visited' or 'skipped')
    // Then return all stops from that point onwards in sequence
    // This prevents routing back to previous uncompleted stops

    const stops = this.filteredRideStops();
    let firstIncompleteIndex = -1;

    // Find the first stop that is not completed
    for (let i = 0; i < stops.length; i++) {
      const status = stops[i].statusType?.toLowerCase();
      if (status !== 'visited') {
        firstIncompleteIndex = i;
        break;
      }
    }

    // If all stops are completed or no incomplete stop found, return empty array
    if (firstIncompleteIndex === -1) {
      return [];
    }

    // Return all stops from the first incomplete stop onwards
    return stops.slice(firstIncompleteIndex);
  }

  private zoomToDriverLocation(zoomLevel: number) {
    if (!this.mapInstance || !this.lastDriverLocation) return;
    if (this.enableDriverLocationAutoCentering) {
      this.mapInstance.setCenter({
        lat: this.lastDriverLocation.latitude,
        lng: this.lastDriverLocation.longitude,
      });
    }

    // Use smooth zoom transition
    this.mapInstance.setZoom(zoomLevel);
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Calculate speed in MPH based on the last 5 points from location history
   * Speed = Total Distance / Total Time
   */
  private calculateSpeedFromHistory(locationsHistory: Array<{ latitude: number; longitude: number; timestamp: number }>): number {
    if (!locationsHistory || locationsHistory.length < 2) {
      return 0; // Not enough data to calculate speed
    }

    // Get the last 5 points (or all available if less than 5)
    const pointsToUse = locationsHistory.slice(-5);

    if (pointsToUse.length < 2) {
      return 0;
    }

    // Calculate total distance traveled across these points
    let totalDistanceMeters = 0;
    for (let i = 1; i < pointsToUse.length; i++) {
      const prevPoint = pointsToUse[i - 1];
      const currentPoint = pointsToUse[i];

      const distance = this.calculateDistance(prevPoint.latitude, prevPoint.longitude, currentPoint.latitude, currentPoint.longitude);

      totalDistanceMeters += distance;
    }

    // Calculate total time elapsed (in seconds)
    const firstPoint = pointsToUse[0];
    const lastPoint = pointsToUse.at(-1)!;
    const totalTimeSeconds = lastPoint.timestamp - firstPoint.timestamp;

    // Avoid division by zero
    if (totalTimeSeconds <= 0) {
      return 0;
    }

    // Calculate speed in meters per second
    const speedMetersPerSecond = totalDistanceMeters / totalTimeSeconds;

    // Convert meters per second to miles per hour
    // 1 meter/second = 2.23694 mph
    const speedMPH = speedMetersPerSecond * 2.23694;

    return speedMPH;
  }

  //@endregion
}
